import { useState, useContext } from 'react';
import { Heading } from '../ui';
import { InputModal } from '../modals';
import { GlobalContext } from '../../helpers/context/GlobalContext';
import { team1Color, team2Color } from '../../customTheme';

import type { Round, InputValue, ModalOpenArgs } from '../../types';

interface TeamInputHeadingProps {
  team1Total: number | string;
  team2Total: number | string;
  title: string;
  team1Bids?: InputValue[];
  team2Bids?: InputValue[];
  isEditable?: boolean;
  index?: number;
  isCurrent?: boolean;
  currentRound?: Round;
  roundHistory?: Round[];
  team1Name?: string;
  team2Name?: string;
  onOpenModal?: (args: ModalOpenArgs) => void;
}

function TeamInputHeading({
  team1Total,
  team2Total,
  title,
  team1Bids = [],
  team2Bids = [],
  isEditable = false,
  index,
  isCurrent,
  currentRound,
  roundHistory,
  // team1Name, // Unused parameter
  // team2Name, // Unused parameter
  onOpenModal,
}: TeamInputHeadingProps) {
  const [isTeam1ModalOpen, setIsTeam1ModalOpen] = useState(false);
  const [isTeam2ModalOpen, setIsTeam2ModalOpen] = useState(false);
  const { setCurrentRound, setRoundHistory } = useContext(GlobalContext);

  // Check if neither player on each team went nil
  const team1CanEdit =
    isEditable &&
    team1Bids.every((bid) => bid !== 'Nil' && bid !== 'Blind Nil');
  const team2CanEdit =
    isEditable &&
    team2Bids.every((bid) => bid !== 'Nil' && bid !== 'Blind Nil');

  const handleTeam1Click = () => {
    if (team1CanEdit) {
      if (onOpenModal) {
        onOpenModal({
          fieldToUpdate: 'team1Total',
          type: 'Actual',
          playerName: 'Team 1',
          onCustomUpdate: (val: InputValue) =>
            handleTeamTotalUpdate(1, parseInt(val as string, 10)),
        });
      } else {
        setIsTeam1ModalOpen(true);
      }
    }
  };

  const handleTeam2Click = () => {
    if (team2CanEdit) {
      if (onOpenModal) {
        onOpenModal({
          fieldToUpdate: 'team2Total',
          type: 'Actual',
          playerName: 'Team 2',
          onCustomUpdate: (val: InputValue) =>
            handleTeamTotalUpdate(2, parseInt(val as string, 10)),
        });
      } else {
        setIsTeam2ModalOpen(true);
      }
    }
  };

  // Custom handler for team total updates
  const handleTeamTotalUpdate = (teamNumber: 1 | 2, totalValue: number) => {
    const teamField = `team${teamNumber}Total`;

    if (isCurrent) {
      // Use the team total field name, let GlobalContext handle the individual updates
      setCurrentRound({
        input: String(totalValue) as InputValue,
        fieldToUpdate: teamField,
        currentRound: { ...(currentRound as Round) },
      });
    } else {
      // For historical rounds, update both fields
      const actualTeamField =
        teamNumber === 1 ? 'team1BidsAndActuals' : 'team2BidsAndActuals';

      // Calculate individual values (divide by 2)
      const individualValue = Math.floor(totalValue / 2);
      const remainder = totalValue % 2;

      // Distribute the total between players
      const p1Value = individualValue + remainder; // First player gets the remainder

      // Mark both actuals as auto-generated for this team
      const autoGeneratedKey1 = teamNumber === 1 ? 'team1P1' : 'team2P1';
      const autoGeneratedKey2 = teamNumber === 1 ? 'team1P2' : 'team2P2';

      const updatedRound = {
        ...currentRound,
        [actualTeamField]: {
          ...currentRound![actualTeamField],
          p1Actual: String(p1Value) as InputValue,
          p2Actual: String(individualValue) as InputValue,
        },
        autoGeneratedActuals: {
          ...currentRound!.autoGeneratedActuals,
          [autoGeneratedKey1]: true,
          [autoGeneratedKey2]: true,
        },
      };

      const newRoundHistory = [...roundHistory!];
      newRoundHistory[index!] = updatedRound as Round;
      setRoundHistory(newRoundHistory);
    }
  };

  return (
    <>
      {/* Team 1 Modal */}
      {!onOpenModal && (
        <InputModal
          isCurrent={!!isCurrent}
          playerName="Team 1"
          isOpen={isTeam1ModalOpen}
          setIsModalOpen={setIsTeam1ModalOpen}
          type="Actual"
          typeLabel="Actuals"
          fieldToUpdate="team1Total"
          currentRound={currentRound as Round}
          roundHistory={roundHistory}
          index={index}
          onCustomUpdate={(value) =>
            handleTeamTotalUpdate(1, parseInt(value as string, 10))
          }
        />
      )}

      {/* Team 2 Modal */}
      {!onOpenModal && (
        <InputModal
          isCurrent={!!isCurrent}
          playerName="Team 2"
          isOpen={isTeam2ModalOpen}
          setIsModalOpen={setIsTeam2ModalOpen}
          type="Actual"
          typeLabel="Actuals"
          fieldToUpdate="team2Total"
          currentRound={currentRound as Round}
          roundHistory={roundHistory}
          index={index}
          onCustomUpdate={(value) =>
            handleTeamTotalUpdate(2, parseInt(value as string, 10))
          }
        />
      )}

      <div
        style={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
        }}
      >
        <Heading
          data-cy="team1Total"
          size="3xl"
          pos="absolute"
          mr="50%"
          pt="var(--app-spacing-4)"
          cursor={team1CanEdit ? 'pointer' : 'default'}
          _focus={{ outline: 'none', boxShadow: 'none' }}
          style={{
            border: team1CanEdit ? '1px solid' : 'none',
            borderColor: team1Color,
            borderRadius: team1CanEdit ? 'var(--app-radius-sm)' : '0',
            padding: team1CanEdit ? '1px var(--app-spacing-1)' : '0',
            color: team1Color,
            top: team1CanEdit ? '1px' : '0',
          }}
          onClick={handleTeam1Click}
        >
          {team1Total}
        </Heading>
        <Heading mt="var(--app-spacing-2)" mb="var(--app-spacing-2)" size="md">
          {title}
        </Heading>
        <Heading
          data-cy="team2Total"
          size="3xl"
          pos="absolute"
          ml="50%"
          pt="var(--app-spacing-4)"
          cursor={team2CanEdit ? 'pointer' : 'default'}
          _focus={{ outline: 'none', boxShadow: 'none' }}
          style={{
            border: team2CanEdit ? '1px solid' : 'none',
            borderColor: team2Color,
            borderRadius: team2CanEdit ? 'var(--app-radius-sm)' : '0',
            padding: team2CanEdit ? '1px var(--app-spacing-1)' : '0',
            color: team2Color,
            top: team2CanEdit ? '1px' : '0',
          }}
          onClick={handleTeam2Click}
        >
          {team2Total}
        </Heading>
      </div>
    </>
  );
}

export default TeamInputHeading;
